<template>
  <page-list
    :list="list"
    :columns="templateListColumns || columns"
    :group-actions="groupActions"
    :single-actions="singleActions"
    :showSearchbox="showSearchbox"
    :showGroupActions="showGroupActions"
    :show-single-actions="!isTemplate"
    :export-data-options="exportDataOptions"
    :show-page="!isTemplate" />
</template>

<script>
import { mapGetters } from 'vuex'
import WindowsMixin from '@/mixins/windows'
import ListMixin from '@/mixins/list'
import ResTemplateListMixin from '@/mixins/resTemplateList'
import workflowMixin from '@/mixins/workflow'
import { WORKFLOW_TYPES } from '@/constants/workflow'
import { uuid } from '@/utils/utils'
import { getProjectDomainFilter, getEnabledFilter, getCreatedAtFilter } from '@/utils/common/tableFilter'
import {
  getSetPublicAction,
} from '@/utils/common/tableActions'
import GlobalSearchMixin from '@/mixins/globalSearch'
import SingleActionsMixin from '../mixins/singleActions'
import ColumnsMixin from '../mixins/columns'

export default {
  name: 'PolicyList',
  mixins: [WindowsMixin, ListMixin, GlobalSearchMixin, ColumnsMixin, SingleActionsMixin, ResTemplateListMixin, workflowMixin],
  props: {
    id: String,
    getParams: {
      type: Object,
    },
    type: String,
  },
  data () {
    return {
      list: this.$list.createList(this, {
        ctx: this,
        id: this.id,
        resource: 'policies',
        apiVersion: 'v1',
        getParams: this.getParam,
        isTemplate: this.isTemplate,
        templateLimit: this.templateLimit,
        filterOptions: {
          id: {
            label: this.$t('table.title.id'),
          },
          name: {
            label: this.$t('system.text_101'),
            filter: true,
            formatter: val => {
              return `name.contains("${val}")`
            },
          },
          project_domain: getProjectDomainFilter(),
          enabled: getEnabledFilter(),
          created_at: getCreatedAtFilter(),
        },
        responseData: this.responseData,
        hiddenColumns: ['created_at'],
      }),
      groupActions: [
        {
          label: this.$t('system.text_128'),
          permission: 'policies_create',
          action: () => {
            this.$router.push('/policy/create')
          },
          meta: () => {
            return {
              buttonType: 'primary',
            }
          },
        },
        {
          label: this.$t('system.text_226'),
          permission: 'policies_update',
          action: () => {
            this.createDialog('DisableDialog', {
              title: this.$t('system.text_341', [this.$t('dictionary.policy')]),
              name: this.$t('dictionary.policy'),
              columns: this.columns,
              data: this.list.selectedItems,
              workflow: this.buildBatchPolicyWorkflow('disable', this.list.selectedItems),
              ok: async () => {
                try {
                  const response = await this.list.batchPerformAction('disable')
                  return response
                } catch (error) {
                  throw error
                }
              },
            })
          },
          meta: () => {
            let validate = true
            if (this.list.selectedItems.length <= 0) {
              validate = false
            }
            if (this.list.selectedItems.some(item => item.enabled === false)) {
              validate = false
            }
            if (this.list.selectedItems.some(item => !this.isOwnerPublic(item))) {
              validate = false
            }
            return {
              validate,
            }
          },
        },
        {
          label: this.$t('system.text_225'),
          permission: 'policies_update',
          action: () => {
            this.createDialog('DisableDialog', {
              title: this.$t('system.text_342', [this.$t('dictionary.policy')]),
              name: this.$t('dictionary.policy'),
              columns: this.columns,
              data: this.list.selectedItems,
              workflow: this.buildBatchPolicyWorkflow('enable', this.list.selectedItems),
              ok: async () => {
                try {
                  const response = await this.list.batchPerformAction('enable')
                  return response
                } catch (error) {
                  throw error
                }
              },
            })
          },
          meta: () => {
            let validate = true
            if (this.list.selectedItems.length <= 0) {
              validate = false
            }
            if (this.list.selectedItems.some(item => item.enabled === true)) {
              validate = false
            }
            if (this.list.selectedItems.some(item => !this.isOwnerPublic(item))) {
              validate = false
            }
            return {
              validate,
            }
          },
        },
        getSetPublicAction(this, {
          name: this.$t('res.policy'),
          scope: 'domain',
          resource: 'policies',
          apiVersion: 'v1',
          noCandidateDomains: true,
        }, {
          permission: 'policies_perform_public',
          meta: () => {
            if (this.list.selectedItems.some(obj => obj.is_system)) {
              return {
                validate: false,
                tooltip: this.$t('policy.tooltip.system_policy'),
              }
            }
            return {
              validate: this.list.selectedItems.length,
            }
          },
        }),
        {
          label: this.$t('system.text_129'),
          permission: 'policies_delete',
          action: () => {
            this.createDialog('DeleteResDialog', {
              vm: this,
              data: this.list.selectedItems,
              columns: this.columns,
              title: this.$t('system.text_129'),
              name: this.$t('dictionary.policy'),
              onManager: this.onManager,
            })
          },
          meta: () => {
            return {
              validate: this.list.allowDelete(),
            }
          },
        },
      ],
    }
  },
  computed: {
    ...mapGetters(['isAdminMode', 'userInfo']),
    exportDataOptions () {
      return {
        title: this.$t('dictionary.policy'),
        downloadType: 'local',
        items: [
          { label: 'ID', field: 'id' },
          ...this.columns,
        ],
      }
    },
  },
  watch: {
    type (val) {
      this.$nextTick(() => {
        this.list.fetchData(0)
      })
    },
  },
  created () {
    this.initSidePageTab('policy-detail')
    this.list.fetchData()
  },
  methods: {
    getParam () {
      const ret = {
        ...this.getParams,
        details: true,
      }
      if (this.type === 'custom') {
        ret.is_system = false
        return ret
      }
      if (this.type === 'system') {
        ret.is_system = true
        return ret
      }
      return ret
    },
    isOwnerPublic (obj) {
      // fix by http://bug.yunion.io/zentao/bug-view-2958.html 共享的权限在其他域下时应该不能做任何操作
      if (obj.is_public) {
        if (this.isAdminMode) return true
        if (obj.domain_id !== this.userInfo.domain.id) return false
      }
      return true
    },
    handleOpenSidepage (row) {
      this.sidePageTriggerHandle(this, 'PolicySidePage', {
        id: row.id,
        resource: 'policies',
        apiVersion: 'v1',
        getParams: this.getParam,
      }, {
        list: this.list,
      })
    },
    buildBatchPolicyWorkflow (action, items) {
      const workflowEnabled = this.checkWorkflowEnabled(action === 'enable' ? WORKFLOW_TYPES.EXECUTE_POLICY_ENABLE : WORKFLOW_TYPES.EXECUTE_POLICY_DISABLE)
      const { displayname, name } = this.userInfo
      const workflowNameKey = action === 'enable' ? 'iam.policy_enable_workflow_name' : 'iam.policy_disable_workflow_name'

      return {
        enabled: workflowEnabled,
        submitText: this.$t('system.text_532'),
        orderSetBuilder: () => {
          const parameters = items.map(item => {
            const projectId = item.project_id || this.userInfo.projectId || ''
            const domainId = item.domain_id || this.userInfo.projectDomainId || 'default'
            const projectDomainName = item.project_domain || (domainId === 'default' ? 'Default' : domainId)
            return {
              action: 'perform',
              perform_action_name: action,
              resource: 'policies',
              resource_id: item.id,
              project: projectId || '',
              project_domain: projectDomainName,
              parameter: {},
            }
          })
          return {
            auto_execute: false,
            name: this.$t(workflowNameKey, [displayname || name, this.$moment().format('YYYY-MM-DD'), uuid()]),
            parameters,
          }
        },
        variablesBuilder: (orderSetRes) => {
          // 使用第一个策略的项目和项目域信息
          const firstItem = items[0]
          const projectId = firstItem.project_id || this.userInfo.projectId || ''
          const domainId = firstItem.domain_id || this.userInfo.projectDomainId || 'default'
          return {
            process_definition_key: action === 'enable' ? WORKFLOW_TYPES.EXECUTE_POLICY_ENABLE : WORKFLOW_TYPES.EXECUTE_POLICY_DISABLE,
            initiator: this.userInfo.id,
            ids: orderSetRes.data.id,
            parameter: '{}',
            project: projectId || '',
            project_domain: domainId,
            execute_resource_order_set_type: action === 'enable' ? 'policies_perform_enable' : 'policies_perform_disable',
          }
        },
        onSuccess: () => {
          this.list.refresh()
        },
      }
    },
  },
}
</script>
